/**
 * Utility functions to process, trim, rotate and normalize signature images to landscape format.
 */

/**
 * Rotates an image data URL by specified angle (default 90 deg clockwise).
 */
export const rotateImageDataUrl = (dataUrl, degrees = 90) => {
  return new Promise((resolve) => {
    if (!dataUrl || typeof dataUrl !== "string" || !dataUrl.startsWith("data:image")) {
      return resolve(dataUrl || "");
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const is90or270 = Math.abs(degrees % 180) === 90;
      canvas.width = is90or270 ? img.height : img.width;
      canvas.height = is90or270 ? img.width : img.height;
      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((degrees * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
};

/**
 * Ensures that the signature data URL is oriented horizontally in landscape format.
 * If height > width (portrait / drawn vertically), it auto-rotates 90 degrees clockwise.
 */
export const ensureLandscapeSignature = (dataUrl) => {
  return new Promise((resolve) => {
    if (!dataUrl || typeof dataUrl !== "string" || !dataUrl.startsWith("data:image")) {
      return resolve(dataUrl || "");
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (img.height > img.width) {
        const canvas = document.createElement("canvas");
        canvas.width = img.height;
        canvas.height = img.width;
        const ctx = canvas.getContext("2d");
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((90 * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        resolve(canvas.toDataURL("image/png"));
      } else {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
};

/**
 * Extracts a trimmed, landscape-oriented signature data URL from a SignatureCanvas ref.
 */
export const extractLandscapeSignature = async (padRef, fallbackValue = "") => {
  if (!padRef || !padRef.current || (typeof padRef.current.isEmpty === "function" && padRef.current.isEmpty())) {
    if (fallbackValue && typeof fallbackValue === "string" && fallbackValue.startsWith("data:image")) {
      return await ensureLandscapeSignature(fallbackValue);
    }
    return fallbackValue || "";
  }
  const pad = padRef.current;
  let dataUrl = "";
  if (typeof pad.getTrimmedCanvas === "function") {
    try {
      dataUrl = pad.getTrimmedCanvas().toDataURL("image/png");
    } catch (e) {
      dataUrl = pad.getCanvas().toDataURL("image/png");
    }
  } else if (typeof pad.getCanvas === "function") {
    dataUrl = pad.getCanvas().toDataURL("image/png");
  }
  return await ensureLandscapeSignature(dataUrl);
};
